import {
  IconButton,
  Modal,
  PersonAvatar,
  Dropdown,
  Switch,
  Label,
  Button,
} from '@teo/components';
import React, { useState, useContext, useEffect, useReducer } from 'react';
import { ChevronDown } from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import { useGet } from '@/query/fetchHooks';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { DataTable } from '@/components/DataTable/DataTable';
import { UserInvite } from '@/components/UserInvite/UserInvite';
import { newUid, nthIndexOf } from '@/util';
import { config } from '@/config';
import { axios } from '@/axios';
import { useNavigate } from 'react-router-dom';
import LibraryAddOrganizations from '@/pages/Protected/Create/PublishCourse/LibraryAddOrganizations';
import { OrganisationContext } from '@/components/Contexts/OrganisationContext';

export const ContentCreatorInviteLP = ({
  onClose,
  learningpath,
  refetchCreatorResults,
  setLearningpath,
  contentCreators,
  onNavigate,
}: any) => {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();

  const [refetchTable, setRefetchTable] = useState<string>(newUid(20));
  const [value, setValue] = useState<any>([]);
  const [oneOwner, setOneOwner] = useState<any>(true);
  const [initialContentOrganizations, setinitialContentOrganizations] =
    useState<any>([]);

  const [isProtected, setIsProtected] = useState(
    learningpath?.lpAccess === 'protected'
  );

  const initialState = {
    isProtected: learningpath?.lpAccess === 'protected',
    users: [],
    allOrganizations: learningpath?.lpAccess === 'public',
    organizations: [],
  };

  const [shareState, setShareState] = useReducer(
    (state: any, updates: any) => ({ ...state, ...updates }),
    initialState
  );

  const [isSaving, setIsSaving] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  const { myOrganisations } = useContext(OrganisationContext);
  let isAdminSomewhereElse = false,
    adminOrganisations = [];
  try {
    adminOrganisations = myOrganisations.filter(
      (org: any) =>
        org.id !== auth?.user?.activeOrganisationId && org.role >= 90
    );
    isAdminSomewhereElse = adminOrganisations.length > 0;
  } catch (e) {
    console.error(e);
  }

  const orgs = useGet(`/learningpathorganisationdetails`, {
    learningpathId: learningpath?.id,
    onSuccess: (result: any) => {
      setinitialContentOrganizations(
        result?.data?.map((item: any) => ({
          ...item,
          key: item.id,
        })) || []
      );
    },
  });

  useEffect(() => {
    if (orgs?.data) {
      const data =
        (orgs?.data?.data as any[]).map((item: any) => ({
          ...item,
          key: item.id,
        })) || [];
      setShareState({ organizations: data });
    }
  }, [orgs?.data]);

  const activeOrganisationId = auth?.user?.activeOrganisationId;

  useEffect(() => {
    if (contentCreators) {
      const allOwner = contentCreators?.filter((user: any) => user?.role >= 90);
      setOneOwner(allOwner?.length <= 1);
    }
  }, [contentCreators]);

  const handleInviteUser = async (values: any[]) => {
    if (isInviting) return;

    setIsInviting(true);

    try {
      for (const item of values) {
        try {
          let userId = item.key ?? item.id ?? item.userId;

          if (item.newUser) {
            const result = await axios.post(
              `${config.backend}/invite/provision?lang=${auth?.user?.lang}`,
              {
                organisationId: activeOrganisationId,
                learningpathId: learningpath?.id,
                email: item.label,
                role: 30,
              }
            );

            userId = result?.data?.id;
          }

          if (!userId) continue;

          await axios.post(`${config.backend}/lpowners`, {
            learningpathId: learningpath?.id,
            userId,
            role: 70,
          });
        } catch (err) {
          console.error('Invite learning path owner failed', item, err);
        }
      }

      setRefetchTable(newUid(15));
      setValue([]);
      refetchCreatorResults();
    } finally {
      setIsInviting(false);
    }
  };

  const handleSave = async () => {
    if (isSaving) return;

    setIsSaving(true);

    try {
      const added = [];
      const deleted = [];
      const changed = [];

      const oldMap: any = new Map(
        initialContentOrganizations.map((item: any) => [item.key, item])
      );
      const newMap: any = new Map(
        shareState.organizations.map((item: any) => [item.key, item])
      );

      for (const [id, newItem] of newMap.entries()) {
        if (!oldMap.has(id)) {
          added.push(newItem);
        } else if (oldMap.get(id).role !== newItem.role) {
          changed.push(newItem);
        }
      }

      for (const [id, oldItem] of oldMap.entries()) {
        if (!newMap.has(id)) {
          deleted.push(oldItem);
        }
      }

      for (const org of added) {
        const organisationId = org.id ?? org.key;
        if (!organisationId) continue;

        try {
          await axios.post(`${config.backend}/memberlearningpaths`, {
            learningpathId: learningpath?.id,
            organisationId,
            role: org.role,
          });
        } catch (err) {
          console.error('Add learning path organisation failed', org, err);
        }
      }

      for (const org of deleted) {
        const organisationId = org.id ?? org.key;
        if (!organisationId) continue;

        try {
          await axios.delete(
            `${config.backend}/memberlearningpath/${organisationId}/${learningpath?.id}`,
            {}
          );
        } catch (err) {
          console.error('Delete learning path organisation failed', org, err);
        }
      }

      for (const org of changed) {
        const organisationId = org.id ?? org.key;
        if (!organisationId) continue;

        try {
          await axios.put(
            `${config.backend}/memberlearningpath/${organisationId}/${learningpath?.id}`,
            {
              role: org.role,
            }
          );
        } catch (err) {
          console.error('Update learning path organisation failed', org, err);
        }
      }

      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleProtected = async () => {
    const newProtected = !isProtected;
    const lpAccess = newProtected ? 'protected' : 'private';

    setIsProtected(newProtected);

    setLearningpath((prev: any) => ({
      ...prev,
      lpAccess,
    }));

    try {
      await axios.put(`${config.backend}/learningpath/${learningpath?.id}`, {
        lpAccess,
      });
    } catch (err) {
      console.error('Update learning path access failed', err);
    }
  };

  return (
    <Modal isOpen={true}>
      <Modal.Header onClose={() => onClose()}>
        <p className="text-sm font-semibold">
          {t('pages.create_edit_course.share_modal_title')}
        </p>
      </Modal.Header>
      <div className="border-b border-grey-02 p-6">
        <>
          <div className="text-sm font-normal">
            <Label htmlFor="invite_author">
              <p className="text-sm font-semibold">
                {t('pages.create_edit_course.multiselect_label')}
              </p>
            </Label>
            <div className="mt-4 text-[#7d7d81]">
              {t(`pages.learning_path.share_modal.add_authors_desc`)}
            </div>
            <UserInvite
              //   publicationType={publicationType}
              fetch={useGet.bind(null, '/invitelearningpaths', {
                learningpathId: learningpath.id,
                organisationId: activeOrganisationId,
              })}
              inviteButtonText={t('pages.create_edit_course.invite_button')}
              value={value}
              setValue={setValue}
              handleAddUserToList={handleInviteUser}
            />
          </div>
          <DataTable
            pageSize={5}
            key={refetchTable}
            defaultSort={[{ id: 'username', desc: false }]}
            fetch={useGet.bind(null, '/lpcontentcreators', {
              learningpathId: learningpath.id,
            })}
            columns={[
              {
                className: 'col-start-1 col-end-6 row-start-1',
                title: t('columns.authors'),
                id: 'authors',
                render: (usr: any) => (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <PersonAvatar
                      inline
                      id={usr.id}
                      name={usr.username}
                      imageSrc={usr.picture}
                    ></PersonAvatar>
                    <div className="flex flex-col">
                      <span className="font-medium">{usr.username}</span>
                      <span className="text-xs font-normal text-[#7D7D81]">
                        {usr.email}
                      </span>
                    </div>
                  </div>
                ),
              },
              {
                className: 'col-start-6 col-end-10 row-start-1 justify-end',
                title: '',
                id: 'authors_role',
                align: 'right',
                render: (usr: any) => {
                  return (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Dropdown>
                        <Dropdown.Trigger className="relative z-[2] flex items-center">
                          {usr.role >= 90 ? (
                            <p>
                              {t('pages.create_edit_course.make_owner_button')}
                            </p>
                          ) : (
                            <p>
                              {t('pages.create_edit_course.may_edit_button')}
                            </p>
                          )}
                          <IconButton Icon={ChevronDown} variant="ghost" />
                        </Dropdown.Trigger>
                        <Dropdown.Content>
                          <Dropdown.Actions>
                            <Dropdown.Button
                              className={`${
                                ((usr.role >= 90 && oneOwner) ||
                                  usr.role === 70) &&
                                'pointer-events-none text-grey-04'
                              }`}
                              onClick={() => {
                                axios
                                  .put(
                                    `${config.backend}/lpowner/${learningpath.id}/${usr.id}`,
                                    {
                                      role: 70,
                                    }
                                  )
                                  .then(() => {
                                    refetchCreatorResults();
                                    setRefetchTable(newUid(20));
                                  });
                              }}
                              disabled={
                                (usr.role >= 90 && oneOwner) || usr.role === 70
                              }
                            >
                              {t('pages.create_edit_course.may_edit_button')}
                            </Dropdown.Button>
                            <Dropdown.Button
                              className={`${
                                usr.role >= 90 &&
                                'pointer-events-none text-grey-04'
                              }`}
                              disabled={usr.role >= 90}
                              onClick={() => {
                                axios
                                  .put(
                                    `${config.backend}/lpowner/${learningpath.id}/${usr.id}`,
                                    {
                                      role: 90,
                                    }
                                  )
                                  .then(() => {
                                    refetchCreatorResults();
                                    setRefetchTable(newUid(20));
                                  });
                              }}
                            >
                              {t('pages.create_edit_course.make_owner_button')}
                            </Dropdown.Button>

                            <Dropdown.Button
                              className={`${
                                usr.role >= 90 && oneOwner
                                  ? 'pointer-events-none text-grey-04'
                                  : 'text-[#FC6232]'
                              }`}
                              disabled={usr.role >= 90 && oneOwner}
                              onClick={() => {
                                axios
                                  .delete(
                                    `${config.backend}/lpowner/${learningpath.id}/${usr.id}`
                                  )
                                  .then(() => {
                                    if (
                                      auth?.user?.id === usr.id &&
                                      (auth?.user?.activeOrganisationRole ||
                                        0) < 90 &&
                                      learningpath?.lpAccess === 'private'
                                    ) {
                                      const basePath =
                                        window.location.pathname.slice(
                                          0,
                                          nthIndexOf(
                                            window.location.pathname,
                                            '/',
                                            3
                                          )
                                        );
                                      onNavigate();
                                      navigate(basePath);
                                    }
                                    setRefetchTable(newUid(20));
                                    refetchCreatorResults();
                                  });
                              }}
                            >
                              <p>
                                {t('pages.create_edit_course.delete_button')}
                              </p>
                            </Dropdown.Button>
                          </Dropdown.Actions>
                        </Dropdown.Content>
                      </Dropdown>
                    </div>
                  );
                },
              },
            ]}
          />

          <div className="mt-6 flex flex-col">
            <Label htmlFor="invite_author">
              <p className="text-sm font-semibold">
                {t('pages.create_edit_course.my_organisation')}
              </p>
            </Label>
            <div className="flex flex-col gap-4 rounded-lg border border-grey-02 p-4">
              <div className="flex flex-row items-center gap-4">
                <Switch checked={isProtected} onChange={handleProtected} />
                <div>
                  <h4 className="pb-2 font-normal">
                    {t('pages.learning_path.publish.make_available')}
                    {auth?.user?.organisationName}
                  </h4>
                  <p className="text-xs text-grey-05">
                    {t(
                      'pages.learning_path.publish.make_available_description'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {((auth?.user?.role || 0) >= 90 || isAdminSomewhereElse) && (
            <div className="mb-2 mt-6">
              <p className="text-sm font-semibold">
                {t(`pages.create_edit_course.other_organisations`)}
              </p>
              <LibraryAddOrganizations
                publication={learningpath}
                state={shareState}
                updateState={setShareState}
                auth={auth}
                getOrg={'/learningpathorganisationdetails'}
                payload={{ learningpathId: learningpath?.id }}
                publicationType={'learning_path'}
              />
            </div>
          )}
        </>
      </div>
      <Modal.Footer cancelButtonLabel={t('button.cancel')} onCancel={onClose}>
        <div>
          <Button disabled={isSaving} onClick={() => handleSave()}>
            {t('button.save')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
